// /admin/reportes/modelo-financiero
// Reporte vivo: kits, productos sueltos, costos operativos, lista de compra mes 1.
// Fuente única de verdad: tablas productos + kits + kit_items + clientes en Supabase.

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

// ─── Supuestos del modelo ──────────────────────────────────────────────────
// (sincronizados con Modelo_Financiero_Limpack_v38.xlsx · pestaña Supuestos)
const SUPUESTOS = {
  // Mix de kits para 60 familias mes 1
  mix_60: { solo: 15, familiar: 14, hogar_plus: 6, duo: 6, confort: 6, exclusive: 3, estudio_oficina: 6, comercio_local: 4 },
  // Logística
  desgaste_zona: 25,         // UYU/kit
  recargo_fuera_zona: 165,   // UYU/kit
  pct_fuera_zona: 0.08,      // 8% solicita fuera de ruta
  capacidad_recorrido: 20,   // kits por viaje
  km_dia: 80,                // promedio MVD
  consumo_lkm: 0.085,        // L/km Note/Onix
  precio_nafta: 80,          // UYU/L
  // Packaging
  bolsa_chica: 9.79,
  bolsa_grande: 21.56,
  etiqueta: 1.50,
  // Procesador de pago
  comision_mp: 0.0599,       // 5.99% MP UY
  // Comisiones porteros
  comision_portero_susc: 50,    // por cliente susc
  comision_portero_unica: 100,  // por compra única
  // Costos fijos mensuales
  cf: { seguro: 3000, mantenimiento: 1500, marketing: 3000, admin: 2000 },
  // Conversión
  pct_susc: 0.80,            // 80% suscripción / 20% one-shot
}

const TIER_COLOR = {
  essential: { bg: '#DBEAFE', fg: '#1E40AF' },
  premium:   { bg: '#FCE7F3', fg: '#9D174D' },
  comercial: { bg: '#D1FAE5', fg: '#065F46' },
}

const fmtUYU = (n, dec = 0) => `$${Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: dec, minimumFractionDigits: dec })}`
const fmtPct = (n, dec = 1) => `${Number(n || 0).toFixed(dec)}%`

export default function ModeloFinanciero() {
  const [tab, setTab] = useState('resumen')
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState({ kits: [], productos: [], clientesActivos: 0, kitItems: [] })

  // Inputs editables del simulador (Fase 2)
  const [sim, setSim] = useState({
    clientes_iniciales: 60,
    crecimiento_neto: 20,    // altas netas mensuales
    churn_pct: 5,            // % cancelaciones / mes
    conversion_susc: 80,     // % suscripción
    pct_adicional: 25,       // % clientes que compran adicionales
    ticket_adicional: 300,   // UYU promedio
  })

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const [{ data: kits }, { data: productos }, { count: clientesActivos }, { data: kitItems }] = await Promise.all([
      supabase.from('kits').select('*').eq('activo', true).order('tier').order('orden'),
      supabase.from('productos').select(`
        id, sku, nombre, marca, presentacion, costo_unitario, precio_publico, precio_mercado,
        stock_actual, vendible_suelto, categoria, grupo_equivalencia,
        proveedor:proveedores(nombre)
      `).eq('activo', true).is('deleted_at', null).order('sku'),
      supabase.from('clientes').select('*', { count: 'exact', head: true }).eq('estado', 'activa'),
      supabase.from('kit_items').select('kit_codigo, cantidad, es_regalo, producto:productos(id, sku, nombre, presentacion, costo_unitario, iva_pct, precio_publico, vendible_suelto, grupo_equivalencia)'),
    ])
    setData({ kits: kits || [], productos: productos || [], clientesActivos: clientesActivos || 0, kitItems: kitItems || [] })
    setLoading(false)
  }

  // ─── Cálculos derivados ─────────────────────────────────────────────────
  const kitsCalc = useMemo(() => {
    return data.kits.map(k => {
      const items = data.kitItems.filter(i => i.kit_codigo === k.codigo && i.producto)
      const costo_siva = items.reduce((s, i) => s + Number(i.producto.costo_unitario || 0) * Number(i.cantidad), 0)
      // costo c/IVA: aplica tasa especifica por producto (10% jabones/detergentes lavavajilla, 22% resto)
      const costo_civa = items.reduce((s, i) => {
        const cu  = Number(i.producto.costo_unitario || 0)
        const iva = Number(i.producto.iva_pct ?? 22)
        return s + cu * (1 + iva / 100) * Number(i.cantidad)
      }, 0)
      // Solo sumamos los productos que el cliente paga: regalos no cuentan en suma_sueltos
      const suma_sueltos = items
        .filter(i => !i.es_regalo)
        .reduce((s, i) => s + Number(i.producto.precio_publico || 0) * Number(i.cantidad), 0)
      const tiene_no_vendibles = items.some(i => !i.producto.vendible_suelto || i.producto.precio_publico == null)
      const margen_susc = Number(k.precio_susc || 0) - costo_civa
      const margen_pct = k.precio_susc > 0 ? (margen_susc / Number(k.precio_susc)) * 100 : 0
      const ahorro_susc = suma_sueltos > 0 ? ((suma_sueltos - Number(k.precio_susc)) / suma_sueltos) * 100 : 0
      const ahorro_unica = suma_sueltos > 0 ? ((suma_sueltos - Number(k.precio_unica)) / suma_sueltos) * 100 : 0
      return { ...k, items, costo_siva, costo_civa, suma_sueltos, tiene_no_vendibles, margen_susc, margen_pct, ahorro_susc, ahorro_unica }
    })
  }, [data])

  // KPIs resumen
  const kpis = useMemo(() => {
    const totalProductos = data.productos.length
    const sueltosVendibles = data.productos.filter(p => p.vendible_suelto && p.precio_publico).length
    const promMargenKit = kitsCalc.length ? kitsCalc.reduce((s, k) => s + k.margen_pct, 0) / kitsCalc.length : 0
    const costoPromKit = kitsCalc.length ? kitsCalc.reduce((s, k) => s + k.costo_civa, 0) / kitsCalc.length : 0
    const precioPromSusc = kitsCalc.length ? kitsCalc.reduce((s, k) => s + Number(k.precio_susc || 0), 0) / kitsCalc.length : 0
    return { totalProductos, sueltosVendibles, promMargenKit, costoPromKit, precioPromSusc }
  }, [data, kitsCalc])

  // Lista de compra mes 1
  const compraMes1 = useMemo(() => {
    const necesidad = {}
    Object.entries(SUPUESTOS.mix_60).forEach(([codigo, n]) => {
      const items = data.kitItems.filter(i => i.kit_codigo === codigo && i.producto)
      items.forEach(i => {
        const sku = i.producto.sku
        if (!necesidad[sku]) necesidad[sku] = { producto: i.producto, demanda: 0 }
        necesidad[sku].demanda += Number(i.cantidad) * n
      })
    })
    // Por grupo de equivalencia: sumar stocks
    const stockGrupos = {}
    data.productos.forEach(p => {
      if (p.grupo_equivalencia) {
        if (!stockGrupos[p.grupo_equivalencia]) stockGrupos[p.grupo_equivalencia] = 0
        stockGrupos[p.grupo_equivalencia] += Number(p.stock_actual || 0)
      }
    })
    return Object.values(necesidad).map(n => {
      const p = n.producto
      const stockEfectivo = p.grupo_equivalencia
        ? stockGrupos[p.grupo_equivalencia]
        : Number(p.stock_actual || 0)
      const comprar = Math.max(Math.ceil(n.demanda - stockEfectivo), 0)
      const gasto = comprar * Number(p.costo_unitario || 0)
      return {
        sku: p.sku, nombre: p.nombre, presentacion: p.presentacion,
        proveedor: data.productos.find(pr => pr.sku === p.sku)?.proveedor?.nombre || '—',
        stock_individual: Number(p.stock_actual || 0),
        stock_efectivo: stockEfectivo,
        demanda: Math.ceil(n.demanda),
        comprar, costo: Number(p.costo_unitario || 0), gasto,
      }
    }).filter(x => x.comprar > 0).sort((a, b) => b.gasto - a.gasto)
  }, [data])

  const totalCompra = compraMes1.reduce((s, x) => s + x.gasto, 0)
  const compraPorProv = useMemo(() => {
    const m = {}
    compraMes1.forEach(x => {
      const k = x.proveedor || 'Sin proveedor'
      if (!m[k]) m[k] = { gasto: 0, items: 0 }
      m[k].gasto += x.gasto; m[k].items += 1
    })
    return Object.entries(m).sort((a, b) => b[1].gasto - a[1].gasto)
  }, [compraMes1])

  // ─── Proyección 12M (Fase 2) ────────────────────────────────────────────
  const proyeccion = useMemo(() => {
    if (!kitsCalc.length) return { meses: [], breakeven: null }

    // Cálculos ponderados según mix
    const totalMix = Object.values(SUPUESTOS.mix_60).reduce((s, v) => s + v, 0) || 1
    const precioSuscPond = kitsCalc.reduce((s, k) => s + Number(k.precio_susc || 0) * (SUPUESTOS.mix_60[k.codigo] || 0), 0) / totalMix
    const precioUnaPond = kitsCalc.reduce((s, k) => s + Number(k.precio_unica || 0) * (SUPUESTOS.mix_60[k.codigo] || 0), 0) / totalMix
    const costoCivaPond = kitsCalc.reduce((s, k) => s + k.costo_civa * (SUPUESTOS.mix_60[k.codigo] || 0), 0) / totalMix
    const packagingPond = ((SUPUESTOS.mix_60.solo + SUPUESTOS.mix_60.comercio_local) * SUPUESTOS.bolsa_chica
      + (totalMix - SUPUESTOS.mix_60.solo - SUPUESTOS.mix_60.comercio_local) * SUPUESTOS.bolsa_grande) / totalMix
    const etiquetaPond = SUPUESTOS.etiqueta

    const meses = []
    let activos = sim.clientes_iniciales
    const meses_label = ['Abr 26','May 26','Jun 26','Jul 26','Ago 26','Sep 26','Oct 26','Nov 26','Dic 26','Ene 27','Feb 27','Mar 27']

    for (let m = 0; m < 12; m++) {
      const inicio = activos
      const susc = Math.round(inicio * (sim.conversion_susc / 100))
      const una = inicio - susc

      // Entregas
      const entregas_zona = Math.round(inicio * (1 - SUPUESTOS.pct_fuera_zona))
      const entregas_fuera = inicio - entregas_zona
      const recorridos = Math.ceil(inicio / SUPUESTOS.capacidad_recorrido)

      // Ingresos
      const ing_susc = susc * precioSuscPond
      const ing_una = una * precioUnaPond
      const ing_envios = entregas_fuera * SUPUESTOS.recargo_fuera_zona
      const ing_adicionales = inicio * (sim.pct_adicional / 100) * sim.ticket_adicional
      const ingresos_total = ing_susc + ing_una + ing_envios + ing_adicionales

      // Costos variables
      const cogs = inicio * costoCivaPond
      const packaging = inicio * (packagingPond + etiquetaPond)
      const log_zona = entregas_zona * SUPUESTOS.desgaste_zona
      const log_fuera = entregas_fuera * SUPUESTOS.desgaste_zona
      const nafta = recorridos * SUPUESTOS.km_dia * SUPUESTOS.consumo_lkm * SUPUESTOS.precio_nafta
      const com_proc = ingresos_total * SUPUESTOS.comision_mp
      const com_porteros = inicio * 0.10 * (susc / inicio * SUPUESTOS.comision_portero_susc + una / inicio * SUPUESTOS.comision_portero_unica)
      const cv_total = cogs + packaging + log_zona + log_fuera + nafta + com_proc + com_porteros

      // Margen
      const margen_bruto = ingresos_total - cv_total
      const cf_total = SUPUESTOS.cf.seguro + SUPUESTOS.cf.mantenimiento + SUPUESTOS.cf.marketing + SUPUESTOS.cf.admin
      const ebitda = margen_bruto - cf_total

      meses.push({
        mes: meses_label[m] || `M${m+1}`,
        activos: inicio, susc, una, recorridos,
        ing_susc, ing_una, ing_envios, ing_adicionales, ingresos_total,
        cogs, packaging, log_zona, log_fuera, nafta, com_proc, com_porteros, cv_total,
        margen_bruto, cf_total, ebitda,
        margen_pct: ingresos_total > 0 ? (margen_bruto / ingresos_total) * 100 : 0,
        ebitda_pct: ingresos_total > 0 ? (ebitda / ingresos_total) * 100 : 0,
      })

      // Crecimiento neto mensual + churn
      const churn_n = Math.round(inicio * (sim.churn_pct / 100))
      activos = inicio - churn_n + sim.crecimiento_neto
    }

    // Totales
    const totales = meses.reduce((acc, m) => {
      Object.keys(m).forEach(k => {
        if (typeof m[k] === 'number' && k !== 'activos') acc[k] = (acc[k] || 0) + m[k]
      })
      return acc
    }, {})
    totales.margen_pct = totales.ingresos_total > 0 ? (totales.margen_bruto / totales.ingresos_total) * 100 : 0
    totales.ebitda_pct = totales.ingresos_total > 0 ? (totales.ebitda / totales.ingresos_total) * 100 : 0

    // Breakeven: primer mes con EBITDA >= 0
    const mes_be = meses.findIndex(m => m.ebitda >= 0)
    const breakeven = {
      precio_susc_pond: precioSuscPond,
      precio_una_pond: precioUnaPond,
      costo_civa_pond: costoCivaPond,
      packaging_pond: packagingPond + etiquetaPond,
      log_pond: SUPUESTOS.desgaste_zona,
      cf_total: SUPUESTOS.cf.seguro + SUPUESTOS.cf.mantenimiento + SUPUESTOS.cf.marketing + SUPUESTOS.cf.admin,
      cv_por_cliente: costoCivaPond + packagingPond + etiquetaPond + SUPUESTOS.desgaste_zona,
      margen_contrib: precioSuscPond - (costoCivaPond + packagingPond + etiquetaPond + SUPUESTOS.desgaste_zona),
      mes_be: mes_be === -1 ? null : mes_be + 1,
    }
    breakeven.clientes_minimos = breakeven.margen_contrib > 0
      ? Math.ceil(breakeven.cf_total / breakeven.margen_contrib) : null
    breakeven.margen_contrib_pct = precioSuscPond > 0 ? (breakeven.margen_contrib / precioSuscPond) * 100 : 0

    return { meses, totales, breakeven }
  }, [kitsCalc, sim])

  // Costos operativos mes 1 (60 clientes)
  const costosOp = useMemo(() => {
    const N = 60
    const entregas_zona = N * (1 - SUPUESTOS.pct_fuera_zona)
    const entregas_fuera = N * SUPUESTOS.pct_fuera_zona
    const recorridos = Math.ceil(N / SUPUESTOS.capacidad_recorrido)
    const km_mes = recorridos * SUPUESTOS.km_dia
    const nafta = km_mes * SUPUESTOS.consumo_lkm * SUPUESTOS.precio_nafta
    const desgaste = entregas_zona * SUPUESTOS.desgaste_zona
    const fuera_recargo = entregas_fuera * SUPUESTOS.recargo_fuera_zona  // ingreso, no costo, pero lo neteo
    const fuera_costo_op = entregas_fuera * SUPUESTOS.desgaste_zona
    const packaging_chico = (SUPUESTOS.mix_60.solo + SUPUESTOS.mix_60.comercio_local) * SUPUESTOS.bolsa_chica
    const packaging_grande = (N - SUPUESTOS.mix_60.solo - SUPUESTOS.mix_60.comercio_local) * SUPUESTOS.bolsa_grande
    const etiquetas = N * SUPUESTOS.etiqueta
    const promPrecioSusc = kpis.precioPromSusc
    const ingresosEstim = N * promPrecioSusc * SUPUESTOS.pct_susc + N * (1 - SUPUESTOS.pct_susc) * promPrecioSusc * 1.18
    const comisionProcesador = ingresosEstim * SUPUESTOS.comision_mp
    const comisionesPorteros = N * 0.10 * SUPUESTOS.comision_portero_susc  // 10% referido por porteros, simplificado
    const totalCF = SUPUESTOS.cf.seguro + SUPUESTOS.cf.mantenimiento + SUPUESTOS.cf.marketing + SUPUESTOS.cf.admin
    return {
      logistica: { recorridos, km_mes, nafta, desgaste, fuera_costo_op, total: nafta + desgaste + fuera_costo_op },
      packaging: { chico: packaging_chico, grande: packaging_grande, etiquetas, total: packaging_chico + packaging_grande + etiquetas },
      comisiones: { procesador: comisionProcesador, porteros: comisionesPorteros, total: comisionProcesador + comisionesPorteros },
      cf: { ...SUPUESTOS.cf, total: totalCF },
    }
  }, [kpis])

  const Tab = ({ id, label, icon }) => (
    <button
      onClick={() => setTab(id)}
      style={{
        background: tab === id ? '#0F1F3D' : '#fff',
        color: tab === id ? '#fff' : '#0F1F3D',
        border: '1px solid ' + (tab === id ? '#0F1F3D' : '#e2e8f0'),
        padding: '8px 14px', borderRadius: 999,
        fontWeight: 700, fontSize: 13, cursor: 'pointer',
      }}
    >{icon} {label}</button>
  )

  if (loading) return <AdminLayout><div className="page"><div className="card">Cargando modelo financiero…</div></div></AdminLayout>

  return (
    <AdminLayout>
      <div className="page">
        <h1>Modelo Financiero — Reporte vivo</h1>
        <p className="lead">Datos en tiempo real desde Supabase · sustituye al Excel v38</p>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '20px 0 18px' }}>
          <Tab id="resumen" label="Resumen" icon="📊" />
          <Tab id="kits" label="Kits y márgenes" icon="🎁" />
          <Tab id="sueltos" label="Productos sueltos" icon="🛒" />
          <Tab id="costos" label="Costos operativos" icon="🚐" />
          <Tab id="compra" label="Compra mes 1" icon="📦" />
          <Tab id="proyeccion" label="Proyección 12M" icon="📈" />
          <Tab id="breakeven" label="Breakeven" icon="⚖️" />
        </div>

        {tab === 'resumen' && <Resumen kpis={kpis} kitsCalc={kitsCalc} clientesActivos={data.clientesActivos} />}
        {tab === 'kits' && <Kits kitsCalc={kitsCalc} />}
        {tab === 'sueltos' && <Sueltos productos={data.productos} />}
        {tab === 'costos' && <Costos costosOp={costosOp} />}
        {tab === 'compra' && <Compra compraMes1={compraMes1} totalCompra={totalCompra} compraPorProv={compraPorProv} />}
        {tab === 'proyeccion' && <Proyeccion sim={sim} setSim={setSim} proyeccion={proyeccion} />}
        {tab === 'breakeven' && <Breakeven proyeccion={proyeccion} sim={sim} />}
      </div>
    </AdminLayout>
  )
}

// ─── TAB: Resumen ───────────────────────────────────────────────────────────
function Resumen({ kpis, kitsCalc, clientesActivos }) {
  const totalSueltos = kitsCalc.reduce((s, k) => s + k.suma_sueltos, 0) / Math.max(kitsCalc.length, 1)
  return (
    <div>
      <h2 className="section-head">📊 Resumen ejecutivo</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <Card label="Clientes activos" value={clientesActivos} sub="Estado activa" color="#1E40AF" />
        <Card label="Productos publicados" value={kpis.sueltosVendibles + ' / ' + kpis.totalProductos} sub="Sueltos / Total" color="#059669" />
        <Card label="Margen prom. kit" value={fmtPct(kpis.promMargenKit)} sub="Susc s/IVA" color="#D97706" />
        <Card label="Costo prom. kit" value={fmtUYU(kpis.costoPromKit)} sub="c/IVA" color="#475569" />
        <Card label="Precio susc prom." value={fmtUYU(kpis.precioPromSusc)} sub="Pesos UYU" color="#7C3AED" />
        <Card label="Promedio sueltos" value={fmtUYU(totalSueltos)} sub="Armar suelto promedio" color="#DC2626" />
      </div>

      <h2 className="section-head" style={{ marginTop: 30 }}>🎯 Salud por kit</h2>
      <table className="t-table">
        <thead>
          <tr>
            <th>Kit</th>
            <th>Tier</th>
            <th style={{ textAlign: 'right' }}>Susc</th>
            <th style={{ textAlign: 'right' }}>Una</th>
            <th style={{ textAlign: 'right' }}>Sueltos</th>
            <th style={{ textAlign: 'right' }}>Ahorro Susc</th>
            <th style={{ textAlign: 'right' }}>Margen Susc</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {kitsCalc.map(k => {
            const color = TIER_COLOR[k.tier] || TIER_COLOR.essential
            const okStatus = k.tiene_no_vendibles ? '🔒 Protegido' : (k.ahorro_susc >= 8 ? '✅ OK' : '⚠ Revisar')
            return (
              <tr key={k.codigo}>
                <td><strong>{k.nombre}</strong></td>
                <td><span style={{ background: color.bg, color: color.fg, padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase' }}>{k.tier}</span></td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtUYU(k.precio_susc)}</td>
                <td style={{ textAlign: 'right' }}>{fmtUYU(k.precio_unica)}</td>
                <td style={{ textAlign: 'right', color: '#64748b' }}>{k.tiene_no_vendibles ? '—' : fmtUYU(k.suma_sueltos)}</td>
                <td style={{ textAlign: 'right', color: k.ahorro_susc >= 8 ? '#059669' : '#DC2626', fontWeight: 700 }}>
                  {k.tiene_no_vendibles ? '—' : fmtPct(k.ahorro_susc)}
                </td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: k.margen_pct >= 35 ? '#059669' : '#D97706' }}>{fmtPct(k.margen_pct)}</td>
                <td>{okStatus}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── TAB: Kits ─────────────────────────────────────────────────────────────
function Kits({ kitsCalc }) {
  return (
    <div>
      <h2 className="section-head">🎁 Composición y márgenes por kit</h2>
      {kitsCalc.map(k => {
        const color = TIER_COLOR[k.tier] || TIER_COLOR.essential
        return (
          <div key={k.codigo} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              <div>
                <strong style={{ fontSize: 17 }}>{k.nombre}</strong>
                <span style={{ background: color.bg, color: color.fg, padding: '3px 10px', borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', marginLeft: 8 }}>{k.tier}</span>
                {k.tiene_no_vendibles && <span style={{ marginLeft: 8, fontSize: 11, color: '#7C3AED', fontWeight: 700 }}>🔒 con productos no-vendibles sueltos</span>}
              </div>
              <div style={{ display: 'flex', gap: 12, fontSize: 13 }}>
                <span><span style={{ color: '#94a3b8' }}>Susc:</span> <strong>{fmtUYU(k.precio_susc)}</strong></span>
                <span><span style={{ color: '#94a3b8' }}>Una:</span> <strong>{fmtUYU(k.precio_unica)}</strong></span>
                <span><span style={{ color: '#94a3b8' }}>Costo c/IVA:</span> <strong>{fmtUYU(k.costo_civa)}</strong></span>
                <span><span style={{ color: '#94a3b8' }}>Margen:</span> <strong style={{ color: k.margen_pct >= 35 ? '#059669' : '#D97706' }}>{fmtPct(k.margen_pct)}</strong></span>
              </div>
            </div>
            {!k.tiene_no_vendibles && (
              <div style={{ background: k.ahorro_susc >= 8 ? '#ECFDF5' : '#FEF3C7', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 10 }}>
                Suma sueltos: <strong>{fmtUYU(k.suma_sueltos)}</strong> · Ahorro suscribiéndose: <strong>{fmtPct(k.ahorro_susc)}</strong> · Una vs sueltos: <strong>{fmtPct(k.ahorro_unica)}</strong>
              </div>
            )}
            <table className="t-table" style={{ marginTop: 0 }}>
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Producto</th>
                  <th>Pres.</th>
                  <th style={{ textAlign: 'right' }}>Cant</th>
                  <th style={{ textAlign: 'right' }}>Costo u.</th>
                  <th style={{ textAlign: 'right' }}>PVP suelto</th>
                  <th style={{ textAlign: 'right' }}>Subtotal costo</th>
                </tr>
              </thead>
              <tbody>
                {k.items.map(i => (
                  <tr key={i.producto.sku}>
                    <td>{i.producto.sku}</td>
                    <td>{i.producto.nombre}</td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{i.producto.presentacion || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>×{Number(i.cantidad)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtUYU(i.producto.costo_unitario, 2)}</td>
                    <td style={{ textAlign: 'right' }}>{i.producto.precio_publico ? fmtUYU(i.producto.precio_publico) : '🔒 no vendible'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtUYU(Number(i.producto.costo_unitario || 0) * Number(i.cantidad))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      })}
    </div>
  )
}

// ─── TAB: Productos sueltos ──────────────────────────────────────────────
function Sueltos({ productos }) {
  const [filtroProv, setFiltroProv] = useState('todos')
  const [orden, setOrden] = useState('sku')
  const proveedores = [...new Set(productos.map(p => p.proveedor?.nombre || 'Sin proveedor'))].sort()

  const filtrados = productos
    .filter(p => p.vendible_suelto && p.precio_publico)
    .filter(p => filtroProv === 'todos' || (p.proveedor?.nombre || 'Sin proveedor') === filtroProv)
    .sort((a, b) => {
      if (orden === 'margen') return ((Number(b.precio_publico) - Number(b.costo_unitario)) / Number(b.precio_publico)) - ((Number(a.precio_publico) - Number(a.costo_unitario)) / Number(a.precio_publico))
      if (orden === 'precio') return Number(b.precio_publico) - Number(a.precio_publico)
      return a.sku.localeCompare(b.sku)
    })

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 className="section-head" style={{ margin: 0 }}>🛒 Productos sueltos vendibles ({filtrados.length})</h2>
        <select value={filtroProv} onChange={e => setFiltroProv(e.target.value)} style={{ padding: 6, borderRadius: 6 }}>
          <option value="todos">Todos los proveedores</option>
          {proveedores.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={orden} onChange={e => setOrden(e.target.value)} style={{ padding: 6, borderRadius: 6 }}>
          <option value="sku">Ordenar por SKU</option>
          <option value="margen">Ordenar por margen</option>
          <option value="precio">Ordenar por precio</option>
        </select>
      </div>
      <table className="t-table">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Producto</th>
            <th>Marca</th>
            <th>Pres.</th>
            <th>Proveedor</th>
            <th style={{ textAlign: 'right' }}>Costo s/IVA</th>
            <th style={{ textAlign: 'right' }}>PVP c/IVA</th>
            <th style={{ textAlign: 'right' }}>Margen $</th>
            <th style={{ textAlign: 'right' }}>Margen %</th>
            <th style={{ textAlign: 'right' }}>vs Mercado</th>
          </tr>
        </thead>
        <tbody>
          {filtrados.map(p => {
            const margenPesos = Number(p.precio_publico) - Number(p.costo_unitario)
            const margenPct = Number(p.precio_publico) > 0 ? (margenPesos / Number(p.precio_publico)) * 100 : 0
            const vsMercado = p.precio_mercado > 0 ? ((Number(p.precio_publico) - Number(p.precio_mercado)) / Number(p.precio_mercado)) * 100 : null
            return (
              <tr key={p.sku}>
                <td><strong>{p.sku}</strong></td>
                <td>{p.nombre}</td>
                <td style={{ color: '#64748b', fontSize: 12 }}>{p.marca || '—'}</td>
                <td style={{ color: '#64748b', fontSize: 12 }}>{p.presentacion || '—'}</td>
                <td style={{ color: '#64748b', fontSize: 12 }}>{p.proveedor?.nombre || '—'}</td>
                <td style={{ textAlign: 'right' }}>{fmtUYU(p.costo_unitario, 2)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtUYU(p.precio_publico)}</td>
                <td style={{ textAlign: 'right' }}>{fmtUYU(margenPesos)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: margenPct >= 50 ? '#059669' : margenPct >= 35 ? '#D97706' : '#DC2626' }}>{fmtPct(margenPct)}</td>
                <td style={{ textAlign: 'right', color: vsMercado === null ? '#94a3b8' : (vsMercado <= 0 ? '#059669' : '#DC2626') }}>
                  {vsMercado === null ? '—' : (vsMercado > 0 ? '+' : '') + vsMercado.toFixed(1) + '%'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── TAB: Costos operativos ────────────────────────────────────────────
function Costos({ costosOp }) {
  const total = costosOp.logistica.total + costosOp.packaging.total + costosOp.comisiones.total + costosOp.cf.total
  return (
    <div>
      <h2 className="section-head">🚐 Costos operativos mensuales (60 clientes)</h2>
      <p style={{ color: '#64748b', fontSize: 13 }}>Estimación basada en supuestos del modelo. Editables en código.</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
        <SeccionCosto titulo="Logística (transporte)" icon="🚐" color="#059669" total={costosOp.logistica.total}>
          <Linea k="Recorridos / mes" v={costosOp.logistica.recorridos + ' viajes'} />
          <Linea k="Km estimados / mes" v={costosOp.logistica.km_mes + ' km'} />
          <Linea k="Nafta" v={fmtUYU(costosOp.logistica.nafta)} />
          <Linea k="Desgaste vehículo (en zona)" v={fmtUYU(costosOp.logistica.desgaste)} />
          <Linea k="Op. fuera de zona" v={fmtUYU(costosOp.logistica.fuera_costo_op)} />
        </SeccionCosto>

        <SeccionCosto titulo="Packaging" icon="📦" color="#7C3AED" total={costosOp.packaging.total}>
          <Linea k="Bolsas chicas (Solo + Comercio)" v={fmtUYU(costosOp.packaging.chico)} />
          <Linea k="Bolsas grandes (resto)" v={fmtUYU(costosOp.packaging.grande)} />
          <Linea k="Etiquetas autoadhesivas" v={fmtUYU(costosOp.packaging.etiquetas)} />
        </SeccionCosto>

        <SeccionCosto titulo="Comisiones" icon="💳" color="#D97706" total={costosOp.comisiones.total}>
          <Linea k="Mercado Pago (5,99%)" v={fmtUYU(costosOp.comisiones.procesador)} />
          <Linea k="Porteros referidos" v={fmtUYU(costosOp.comisiones.porteros)} />
        </SeccionCosto>

        <SeccionCosto titulo="Costos fijos mensuales" icon="🏗️" color="#1E40AF" total={costosOp.cf.total}>
          <Linea k="Seguro vehículos (Note + Onix)" v={fmtUYU(costosOp.cf.seguro)} />
          <Linea k="Mantenimiento / imprevistos" v={fmtUYU(costosOp.cf.mantenimiento)} />
          <Linea k="Marketing y publicidad" v={fmtUYU(costosOp.cf.marketing)} />
          <Linea k="Administración y otros" v={fmtUYU(costosOp.cf.admin)} />
        </SeccionCosto>
      </div>

      <div style={{ marginTop: 24, background: '#0F1F3D', color: '#fff', padding: 18, borderRadius: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700 }}>Total costos operativos / mes (60 clientes)</div>
            <div style={{ fontSize: 28, fontWeight: 900 }}>{fmtUYU(total)}</div>
          </div>
          <div style={{ fontSize: 13, opacity: 0.85, textAlign: 'right' }}>
            <div>Variable: {fmtUYU(costosOp.logistica.total + costosOp.packaging.total + costosOp.comisiones.total)}</div>
            <div>Fijos: {fmtUYU(costosOp.cf.total)}</div>
            <div>Costo / cliente: <strong>{fmtUYU(total / 60)}</strong></div>
          </div>
        </div>
      </div>
    </div>
  )
}

function SeccionCosto({ titulo, icon, color, total, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color }}>{icon} {titulo}</div>
        <div style={{ fontSize: 19, fontWeight: 900, color }}>{fmtUYU(total)}</div>
      </div>
      <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>{children}</div>
    </div>
  )
}

function Linea({ k, v }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0' }}>
      <span style={{ color: '#64748b' }}>{k}</span>
      <span style={{ fontWeight: 700 }}>{v}</span>
    </div>
  )
}

// ─── TAB: Compra mes 1 ────────────────────────────────────────────────
function Compra({ compraMes1, totalCompra, compraPorProv }) {
  return (
    <div>
      <h2 className="section-head">📦 Lista de compra · 60 kits mes 1</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        {compraPorProv.map(([prov, d]) => (
          <Card key={prov} label={prov} value={fmtUYU(d.gasto)} sub={`${d.items} ítems`} color="#475569" />
        ))}
        <Card label="TOTAL" value={fmtUYU(totalCompra)} sub={`${compraMes1.length} ítems · s/IVA`} color="#0F1F3D" />
      </div>

      <table className="t-table">
        <thead>
          <tr>
            <th>Proveedor</th>
            <th>SKU</th>
            <th>Producto</th>
            <th>Pres.</th>
            <th style={{ textAlign: 'right' }}>Stock</th>
            <th style={{ textAlign: 'right' }}>Necesita</th>
            <th style={{ textAlign: 'right' }}>Comprar</th>
            <th style={{ textAlign: 'right' }}>$ unit</th>
            <th style={{ textAlign: 'right' }}>Gasto</th>
          </tr>
        </thead>
        <tbody>
          {compraMes1.map(x => (
            <tr key={x.sku}>
              <td style={{ fontSize: 12, color: '#64748b' }}>{x.proveedor}</td>
              <td><strong>{x.sku}</strong></td>
              <td>{x.nombre}</td>
              <td style={{ fontSize: 12, color: '#64748b' }}>{x.presentacion || '—'}</td>
              <td style={{ textAlign: 'right' }}>
                {x.stock_individual}
                {x.stock_efectivo !== x.stock_individual && (
                  <span style={{ fontSize: 10, color: '#7C3AED', marginLeft: 4 }}>(grupo: {x.stock_efectivo})</span>
                )}
              </td>
              <td style={{ textAlign: 'right' }}>{x.demanda}</td>
              <td style={{ textAlign: 'right', fontWeight: 800 }}>{x.comprar}</td>
              <td style={{ textAlign: 'right' }}>{fmtUYU(x.costo, 2)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtUYU(x.gasto)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Card({ label, value, sub, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.04em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 900, color: color || '#0F1F3D', marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

// ─── TAB: Proyección 12 meses ────────────────────────────────────────
function Proyeccion({ sim, setSim, proyeccion }) {
  const { meses = [], totales = {} } = proyeccion
  const update = (k, v) => setSim(s => ({ ...s, [k]: Number(v) }))

  return (
    <div>
      <h2 className="section-head">📈 Simulador de proyección 12 meses</h2>
      <p style={{ color: '#64748b', fontSize: 13 }}>Movés los inputs y todo se recalcula al instante.</p>

      {/* Inputs editables */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, marginBottom: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
          <Slider label="Clientes iniciales (mes 1)" value={sim.clientes_iniciales} min={10} max={300} step={5} onChange={v => update('clientes_iniciales', v)} suffix="" />
          <Slider label="Crecimiento neto / mes" value={sim.crecimiento_neto} min={0} max={100} step={1} onChange={v => update('crecimiento_neto', v)} suffix="" />
          <Slider label="Churn mensual" value={sim.churn_pct} min={0} max={20} step={0.5} onChange={v => update('churn_pct', v)} suffix="%" />
          <Slider label="Conversión suscripción" value={sim.conversion_susc} min={50} max={100} step={1} onChange={v => update('conversion_susc', v)} suffix="%" />
          <Slider label="% que compra adicionales" value={sim.pct_adicional} min={0} max={60} step={1} onChange={v => update('pct_adicional', v)} suffix="%" />
          <Slider label="Ticket adicional promedio" value={sim.ticket_adicional} min={50} max={1000} step={10} onChange={v => update('ticket_adicional', v)} suffix=" $" />
        </div>
      </div>

      {/* KPIs anuales */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
        <Card label="Clientes mes 12" value={meses[meses.length - 1]?.activos ?? '—'} sub="Final del año 1" color="#1E40AF" />
        <Card label="Ingresos 12M" value={fmtUYU(totales.ingresos_total)} sub="Total año" color="#059669" />
        <Card label="Margen bruto" value={fmtUYU(totales.margen_bruto)} sub={fmtPct(totales.margen_pct) + ' s/ingresos'} color="#D97706" />
        <Card label="EBITDA 12M" value={fmtUYU(totales.ebitda)} sub={fmtPct(totales.ebitda_pct) + ' s/ingresos'} color="#7C3AED" />
        <Card label="Costos fijos 12M" value={fmtUYU(totales.cf_total)} sub="Seguro + Mant + Mkt + Admin" color="#475569" />
      </div>

      {/* Tabla mensual */}
      <h3 className="section-head" style={{ fontSize: 15 }}>Detalle mes a mes</h3>
      <div style={{ overflowX: 'auto' }}>
        <table className="t-table" style={{ minWidth: 1200, fontSize: 12 }}>
          <thead>
            <tr>
              <th>Mes</th>
              <th style={{ textAlign: 'right' }}>Clientes</th>
              <th style={{ textAlign: 'right' }}>Susc</th>
              <th style={{ textAlign: 'right' }}>Una</th>
              <th style={{ textAlign: 'right' }}>Ing. susc</th>
              <th style={{ textAlign: 'right' }}>Ing. una</th>
              <th style={{ textAlign: 'right' }}>Ing. envíos</th>
              <th style={{ textAlign: 'right' }}>Adicionales</th>
              <th style={{ textAlign: 'right' }}>Ingresos</th>
              <th style={{ textAlign: 'right' }}>CV total</th>
              <th style={{ textAlign: 'right' }}>Margen</th>
              <th style={{ textAlign: 'right' }}>%MB</th>
              <th style={{ textAlign: 'right' }}>EBITDA</th>
              <th style={{ textAlign: 'right' }}>%EB</th>
            </tr>
          </thead>
          <tbody>
            {meses.map((m, i) => (
              <tr key={i}>
                <td><strong>{m.mes}</strong></td>
                <td style={{ textAlign: 'right' }}>{m.activos}</td>
                <td style={{ textAlign: 'right', color: '#64748b' }}>{m.susc}</td>
                <td style={{ textAlign: 'right', color: '#64748b' }}>{m.una}</td>
                <td style={{ textAlign: 'right' }}>{fmtUYU(m.ing_susc)}</td>
                <td style={{ textAlign: 'right' }}>{fmtUYU(m.ing_una)}</td>
                <td style={{ textAlign: 'right' }}>{fmtUYU(m.ing_envios)}</td>
                <td style={{ textAlign: 'right' }}>{fmtUYU(m.ing_adicionales)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtUYU(m.ingresos_total)}</td>
                <td style={{ textAlign: 'right', color: '#DC2626' }}>{fmtUYU(m.cv_total)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtUYU(m.margen_bruto)}</td>
                <td style={{ textAlign: 'right' }}>{fmtPct(m.margen_pct, 0)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: m.ebitda >= 0 ? '#059669' : '#DC2626' }}>{fmtUYU(m.ebitda)}</td>
                <td style={{ textAlign: 'right' }}>{fmtPct(m.ebitda_pct, 0)}</td>
              </tr>
            ))}
            <tr style={{ background: '#0F1F3D', color: '#fff', fontWeight: 800 }}>
              <td>TOTAL 12M</td>
              <td style={{ textAlign: 'right' }}>—</td>
              <td style={{ textAlign: 'right' }}>{Math.round(totales.susc || 0)}</td>
              <td style={{ textAlign: 'right' }}>{Math.round(totales.una || 0)}</td>
              <td style={{ textAlign: 'right' }}>{fmtUYU(totales.ing_susc)}</td>
              <td style={{ textAlign: 'right' }}>{fmtUYU(totales.ing_una)}</td>
              <td style={{ textAlign: 'right' }}>{fmtUYU(totales.ing_envios)}</td>
              <td style={{ textAlign: 'right' }}>{fmtUYU(totales.ing_adicionales)}</td>
              <td style={{ textAlign: 'right' }}>{fmtUYU(totales.ingresos_total)}</td>
              <td style={{ textAlign: 'right' }}>{fmtUYU(totales.cv_total)}</td>
              <td style={{ textAlign: 'right' }}>{fmtUYU(totales.margen_bruto)}</td>
              <td style={{ textAlign: 'right' }}>{fmtPct(totales.margen_pct, 0)}</td>
              <td style={{ textAlign: 'right' }}>{fmtUYU(totales.ebitda)}</td>
              <td style={{ textAlign: 'right' }}>{fmtPct(totales.ebitda_pct, 0)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Slider({ label, value, min, max, step, onChange, suffix }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</span>
        <strong style={{ fontSize: 16, color: '#0F1F3D' }}>{value}{suffix}</strong>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(e.target.value)}
        style={{ width: '100%', cursor: 'pointer' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
        <span>{min}</span><span>{max}</span>
      </div>
    </div>
  )
}

// ─── TAB: Breakeven ─────────────────────────────────────────────────
function Breakeven({ proyeccion, sim }) {
  const be = proyeccion.breakeven
  if (!be) return <div className="card">Sin datos suficientes.</div>

  return (
    <div>
      <h2 className="section-head">⚖️ Punto de equilibrio</h2>
      <p style={{ color: '#64748b', fontSize: 13 }}>Cuántos clientes hacen falta para cubrir costos fijos. Recalcula al cambiar inputs en "Proyección".</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 22 }}>
        <Card label="Clientes mínimos" value={be.clientes_minimos ?? '—'} sub="Para cubrir costos fijos" color="#DC2626" />
        <Card label="Margen contribución" value={fmtUYU(be.margen_contrib)} sub={fmtPct(be.margen_contrib_pct) + ' por cliente'} color="#059669" />
        <Card label="Costos fijos / mes" value={fmtUYU(be.cf_total)} sub="A cubrir cada mes" color="#475569" />
        <Card label="Mes de breakeven" value={be.mes_be ? `Mes ${be.mes_be}` : 'No alcanza en 12M'} sub={`Con ${sim.clientes_iniciales} iniciales · +${sim.crecimiento_neto}/mes`} color={be.mes_be ? '#059669' : '#DC2626'} />
      </div>

      <h3 className="section-head" style={{ fontSize: 15 }}>Cálculo paso a paso</h3>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18 }}>
        <table className="t-table" style={{ marginTop: 0 }}>
          <tbody>
            <BeRow k="Precio suscripción promedio ponderado" v={fmtUYU(be.precio_susc_pond)} note="kit_susc × mix" />
            <BeRow k="(−) Costo de productos c/IVA" v={fmtUYU(be.costo_civa_pond)} note="costo s/IVA × 1.22" minus />
            <BeRow k="(−) Packaging por cliente" v={fmtUYU(be.packaging_pond)} note="bolsa + etiqueta" minus />
            <BeRow k="(−) Logística (desgaste vehículo)" v={fmtUYU(be.log_pond)} note="por entrega en zona" minus />
            <tr style={{ background: '#F0FDF4', fontWeight: 800 }}>
              <td>= Margen de contribución</td>
              <td style={{ textAlign: 'right', color: '#059669' }}>{fmtUYU(be.margen_contrib)}</td>
              <td style={{ fontSize: 11, color: '#64748b' }}>por cliente · {fmtPct(be.margen_contrib_pct)}</td>
            </tr>
            <BeRow k="Costos fijos mensuales" v={fmtUYU(be.cf_total)} note="seguro + mantenim + mkt + admin" />
            <tr style={{ background: '#0F1F3D', color: '#fff', fontWeight: 800 }}>
              <td>CLIENTES MÍNIMOS = CF / Margen contribución</td>
              <td style={{ textAlign: 'right' }}>{be.clientes_minimos ?? '—'}</td>
              <td style={{ fontSize: 11, opacity: 0.85 }}>{fmtUYU(be.cf_total)} / {fmtUYU(be.margen_contrib)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 18, background: be.mes_be ? '#ECFDF5' : '#FEF2F2', border: be.mes_be ? '1px solid #6EE7B7' : '1px solid #FCA5A5', padding: 14, borderRadius: 10 }}>
        <strong style={{ color: be.mes_be ? '#065F46' : '#7F1D1D' }}>
          {be.mes_be
            ? `✅ Con ${sim.clientes_iniciales} clientes iniciales, llegás al breakeven en el mes ${be.mes_be}.`
            : `❌ Con la configuración actual (${sim.clientes_iniciales} iniciales, +${sim.crecimiento_neto}/mes, churn ${sim.churn_pct}%) NO se llega al breakeven en 12 meses.`}
        </strong>
        <div style={{ fontSize: 13, marginTop: 4, color: be.mes_be ? '#065F46' : '#7F1D1D' }}>
          {be.clientes_minimos
            ? `Necesitás al menos ${be.clientes_minimos} clientes activos para cubrir costos fijos. Hoy estás en ${sim.clientes_iniciales}.`
            : 'El margen de contribución es cero o negativo — revisar precios/costos.'}
        </div>
      </div>
    </div>
  )
}

function BeRow({ k, v, note, minus }) {
  return (
    <tr>
      <td style={{ color: minus ? '#DC2626' : '#0F1F3D' }}>{k}</td>
      <td style={{ textAlign: 'right', color: minus ? '#DC2626' : '#0F1F3D', fontWeight: 700 }}>{minus ? '−' : ''}{v}</td>
      <td style={{ fontSize: 11, color: '#64748b' }}>{note}</td>
    </tr>
  )
}
